import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AtomicButton from '../../atomic/button/Button';

const NAVY = '#1B1642';
const INK = '#3F3F6E';
const YELLOW = '#fce76b';
const MINT = '#B8ECD7';
const LINE = '#d9d7e6';

const useIsMobile = (breakpoint = 1024) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const WINS = [
  { title: 'Bigger tickets', text: 'Every combo sells four items, not one.' },
  {
    title: 'Predictable volume',
    text: 'Steady combo orders make prep and staffing easier to plan.',
  },
  {
    title: 'Free delivery, your call',
    text: 'You decide when to offer it. We recommend free delivery on Trio and Family combos, where larger orders make it worth it.',
  },
];

const COURSES = [
  { icon: '🥗', label: 'Starter' },
  { icon: '🍛', label: 'Main' },
  { icon: '🍰', label: 'Dessert' },
  { icon: '🥤', label: 'Drink' },
];

const TIERS = [
  { name: 'Solo', who: '1 person', price: '$15.99' },
  { name: 'Duo', who: '2 people', price: '$29.99' },
  { name: 'Trio', who: '3 people', price: '$42.99', freeDelivery: true },
  { name: 'Family', who: '4 people', price: '$54.99', freeDelivery: true },
];

const Tick = () => (
  <span
    style={{
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: NAVY,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden='true'>
      <path
        d='M2 7.5l3 3 7-7'
        stroke={YELLOW}
        strokeWidth='2.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </span>
);

const HomeTopicFour = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <section style={{ backgroundColor: YELLOW, width: '100%' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '44px' : '90px',
          alignItems: 'center',
          maxWidth: '1280px',
          margin: '0 auto',
          padding: isMobile ? '48px 22px 56px' : '110px 120px',
          boxSizing: 'border-box',
          color: NAVY,
        }}
      >
        {/* Text */}
        <div>
          <h2
            style={{
              margin: 0,
              fontFamily: 'Inter Bold',
              fontWeight: 800,
              letterSpacing: '-0.025em',
              fontSize: isMobile ? '34px' : '56px',
              lineHeight: isMobile ? 1.08 : 1.02,
            }}
          >
            Your menu, sold as complete meals
          </h2>
          <p
            style={{
              margin: '18px 0 0',
              color: INK,
              fontFamily: 'Inter Regular',
              fontSize: isMobile ? '17px' : '20px',
              lineHeight: 1.5,
              maxWidth: '27em',
            }}
          >
            Bundle your own dishes into combo packages for one to four people. Customers get a full
            meal at a fair price, and you get bigger orders that come in steadily.
          </p>

          <ul
            style={{
              listStyle: 'none',
              margin: '30px 0 0',
              padding: 0,
              display: 'grid',
              gap: '14px',
            }}
          >
            {WINS.map((w) => (
              <li key={w.title} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Tick />
                <div>
                  <div style={{ fontFamily: 'Inter Bold', fontWeight: 700, fontSize: '17px' }}>
                    {w.title}
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter Regular',
                      fontSize: '15px',
                      lineHeight: 1.45,
                      color: INK,
                      marginTop: '2px',
                    }}
                  >
                    {w.text}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <AtomicButton
            label={isMobile ? "Let's Connect" : 'Get started'}
            handleSubmit={() => {
              isMobile ? navigate('/merchant-contact') : navigate('/checkzipcode');
            }}
            style={{
              width: '100%',
              maxWidth: '325px',
              backgroundColor: NAVY,
              color: '#fff',
              marginTop: '34px',
            }}
            loading={false}
            disabled={false}
          />
        </div>

        {/* Menu card */}
        <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '470px',
              backgroundColor: '#fff',
              border: `2px solid ${NAVY}`,
              borderRadius: '6px',
              boxShadow: isMobile ? `7px 7px 0 ${NAVY}` : `10px 10px 0 ${NAVY}`,
              padding: isMobile ? '28px 22px 24px' : '40px 40px 34px',
              boxSizing: 'border-box',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {/* Free delivery stamp */}
            <div
              style={{
                position: 'absolute',
                top: isMobile ? '-34px' : '-44px',
                right: isMobile ? '-10px' : '-30px',
                width: isMobile ? '92px' : '118px',
                height: isMobile ? '92px' : '118px',
                borderRadius: '50%',
                backgroundColor: MINT,
                border: `2px solid ${NAVY}`,
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                transform: 'rotate(-12deg)',
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: 'Inter Bold',
                    fontWeight: 800,
                    fontSize: isMobile ? '18px' : '22px',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Free
                </div>
                <div
                  style={{
                    fontFamily: 'Inter Medium',
                    fontWeight: 600,
                    fontSize: isMobile ? '11px' : '12px',
                    marginTop: '3px',
                  }}
                >
                  delivery on
                  <br />
                  Trio &amp; Family
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: 'Inter Medium',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: INK,
                }}
              >
                Built by your restaurant
              </div>
              <div
                style={{
                  fontFamily: 'Inter Bold',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontSize: isMobile ? '28px' : '34px',
                  marginTop: '4px',
                }}
              >
                Your combo menu
              </div>
            </div>

            {/* Double rule */}
            <div style={{ marginTop: '20px' }}>
              <div style={{ borderTop: `2px solid ${NAVY}` }} />
              <div style={{ borderTop: `1px solid ${NAVY}`, marginTop: '2px' }} />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '6px',
                marginTop: '22px',
                textAlign: 'center',
              }}
            >
              {COURSES.map((c) => (
                <div
                  key={c.label}
                  style={{
                    fontFamily: 'Inter Medium',
                    fontWeight: 600,
                    fontSize: '13px',
                    color: INK,
                  }}
                >
                  <span
                    style={{ display: 'block', fontSize: '24px', marginBottom: '4px' }}
                    aria-hidden='true'
                  >
                    {c.icon}
                  </span>
                  {c.label}
                </div>
              ))}
            </div>
            <div
              style={{
                textAlign: 'center',
                fontFamily: 'Inter Regular',
                fontSize: '13px',
                color: INK,
                marginTop: '12px',
              }}
            >
              Included in every combo
            </div>

            <ul style={{ listStyle: 'none', margin: '20px 0 0', padding: 0 }}>
              {TIERS.map((t, i) => (
                <li
                  key={t.name}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '10px',
                    padding: '14px 0',
                    borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Inter Bold',
                      fontWeight: 700,
                      fontSize: '18px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.name}
                  </span>
                  <span
                    style={{
                      fontFamily: 'Inter Regular',
                      fontSize: '14px',
                      color: INK,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.who}
                  </span>
                  {t.freeDelivery && (
                    <span
                      style={{
                        fontFamily: 'Inter Bold',
                        fontWeight: 700,
                        fontSize: '11px',
                        padding: '3px 7px',
                        borderRadius: '6px',
                        backgroundColor: MINT,
                        whiteSpace: 'nowrap',
                        alignSelf: 'center',
                      }}
                    >
                      Free delivery
                    </span>
                  )}
                  <span
                    style={{
                      flex: 1,
                      minWidth: '16px',
                      borderBottom: '2px dotted #b4b2c8',
                      transform: 'translateY(-4px)',
                    }}
                  />
                  <span style={{ fontFamily: 'Inter Bold', fontWeight: 800, fontSize: '18px' }}>
                    {t.price}
                  </span>
                </li>
              ))}
            </ul>
            <div
              style={{
                textAlign: 'center',
                fontFamily: 'Inter Regular',
                fontSize: '12px',
                color: INK,
                marginTop: '14px',
              }}
            >
              Example combos and prices. You choose what goes in each.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeTopicFour;
